import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, Loader2, X } from "lucide-react";

/**
 * Catalog-wide product search for the Manufacturer Detail page.
 *
 * Debounced typeahead that hits
 *   GET /api/manufacturers/{manufacturerId}/products/search?q=...
 * and shows up to 50 matches (by product name OR model number) grouped
 * by product type. Clicking a result switches the active product-type
 * tab and scrolls/highlights the row via `onPick`.
 */
export default function ManufacturerProductSearch({ manufacturerId, onPick }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await api.get(
          `/manufacturers/${manufacturerId}/products/search`,
          { params: { q: trimmed } }
        );
        setResults(res.data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, manufacturerId]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Group results by product type for friendly display
  const groups = results.reduce((acc, p) => {
    const key = p.product_type_name || "(unknown)";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const handlePick = (p) => {
    setOpen(false);
    setQuery("");
    setResults([]);
    onPick?.(p);
  };

  return (
    <div className="relative" ref={boxRef} data-testid="manufacturer-product-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Find a product by name or model number across all types..."
          className="w-full pl-9 pr-9 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
          data-testid="manufacturer-product-search-input"
        />
        {(loading || query) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <button
                onClick={() => {
                  setQuery("");
                  setResults([]);
                }}
                className="text-muted-foreground hover:text-slate-900"
                aria-label="Clear search"
                data-testid="manufacturer-product-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {open && query.trim().length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-96 overflow-y-auto bg-white border border-border rounded-sm shadow-lg"
          data-testid="manufacturer-product-search-results"
        >
          {!loading && results.length === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">
              No products match <span className="font-mono-data">"{query}"</span>.
              <br />
              <span className="text-xs">
                This product hasn't been added yet, or the name/model number differs.
              </span>
            </div>
          )}
          {results.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest font-mono-data text-muted-foreground bg-slate-50 border-b border-border">
                {results.length} match{results.length === 1 ? "" : "es"} found
              </div>
              {Object.entries(groups).map(([ptName, items]) => (
                <div key={ptName}>
                  <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700 bg-slate-100/70 border-b border-border">
                    {ptName}
                    <span className="ml-1.5 text-muted-foreground font-mono-data">
                      ({items.length})
                    </span>
                  </div>
                  {items.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handlePick(p)}
                      className="w-full text-left px-4 py-2.5 hover:bg-amber-50 border-b border-border last:border-b-0 transition-colors"
                      data-testid={`manufacturer-product-search-result-${p.id}`}
                    >
                      <div className="text-sm font-medium text-slate-900">
                        {p.name || "(unnamed)"}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono-data mt-0.5">
                        {p.model_number || "—"}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
